// @ts-nocheck
/**
 * ダンジョン探索フック
 * @module twinKey/hooks/useDungeon
 */

import { useState, useCallback, useMemo } from 'react';
import { useTwinKeyContext } from '../context/TwinKeyContext';
import {
  getNextFloorChoices,
  advanceFloor,
  completeFloor,
  generateDungeonSummary,
} from '../core';
import { addExperience } from '../core/party/PartyManager';
import { calculateAllStats } from '../core/party/StatCalculator';
import { calculateBattleExp } from '../data/constants';
import DropManager from '../content/drops/DropManager';
import { rewardCalculator } from '../content/drops/rewardCalculator';

/**
 * ダンジョン探索フック
 * @returns {Object} ダンジョン操作API
 */
export function useDungeon() {
  const {
    state,
    actions,
    updatePlayerData,
  } = useTwinKeyContext();

  // フロア選択肢
  const [floorChoices, setFloorChoices] = useState([]);
  // 選択中の選択肢
  const [selectedChoice, setSelectedChoice] = useState(null);
  // ホバー中の選択肢
  const [hoveredChoice, setHoveredChoice] = useState(null);
  // 探索中に収集した素材
  const [collectedMaterials, setCollectedMaterials] = useState({});
  // 探索中に獲得したゴールド
  const [collectedGold, setCollectedGold] = useState(0);

  // ドロップマネージャー
  const dropManager = useMemo(() => new DropManager(), []);

  // ============================================================
  // 現在のダンジョン情報
  // ============================================================

  const dungeon = state.currentDungeon;

  /**
   * 現在の階層
   */
  const currentFloor = useMemo(() => {
    return dungeon?.currentFloor || 0;
  }, [dungeon]);

  /**
   * 最大階層
   */
  const maxFloor = useMemo(() => {
    return dungeon?.maxFloor || 0;
  }, [dungeon]);

  /**
   * 進行度（%）
   */
  const progress = useMemo(() => {
    if (!maxFloor) return 0;
    return Math.floor((currentFloor / maxFloor) * 100);
  }, [currentFloor, maxFloor]);

  /**
   * ボス階層かどうか
   */
  const isBossFloor = useMemo(() => {
    if (!dungeon) return false;
    return dungeon.bossFloors?.includes(currentFloor + 1);
  }, [dungeon, currentFloor]);

  // ============================================================
  // 素材保存
  // ============================================================

  /**
   * 収集した素材とゴールドをプレイヤーデータに保存
   * @param {Object} additionalMaterials - 追加で保存する素材（React状態更新の非同期性対策）
   * @param {number} additionalGold - 追加で保存するゴールド
   */
  const saveMaterialsToPlayerData = useCallback((additionalMaterials = {}, additionalGold = 0) => {
    // 現在の状態と追加分をマージ
    const allMaterials = { ...collectedMaterials };
    for (const [type, count] of Object.entries(additionalMaterials)) {
      allMaterials[type] = (allMaterials[type] || 0) + count;
    }
    const totalGold = collectedGold + additionalGold;

    if (Object.keys(allMaterials).length === 0 && totalGold === 0) {
      return;
    }

    console.log('[DEBUG saveMaterialsToPlayerData] 保存する素材:', allMaterials);
    console.log('[DEBUG saveMaterialsToPlayerData] 保存するゴールド:', totalGold);

    updatePlayerData?.((prev) => {
      const currentMaterials = prev.twinKey?.materials || {};
      const updatedMaterials = { ...currentMaterials };

      // 収集した素材を追加
      for (const [type, count] of Object.entries(allMaterials)) {
        updatedMaterials[type] = (updatedMaterials[type] || 0) + count;
      }

      // ゴールドを追加
      const currentGold = prev.twinKey?.gold || 0;
      const newGold = currentGold + totalGold;

      return {
        ...prev,
        twinKey: {
          ...prev.twinKey,
          materials: updatedMaterials,
          gold: newGold,
        },
      };
    });

    // 収集状態をリセット
    setCollectedMaterials({});
    setCollectedGold(0);
  }, [collectedMaterials, collectedGold, updatePlayerData]);

  // ============================================================
  // フロア進行
  // ============================================================

  /**
   * 次のフロアの選択肢を取得
   */
  const loadNextFloorChoices = useCallback(() => {
    if (!dungeon) return;

    const choices = getNextFloorChoices(dungeon);
    setFloorChoices(choices || []);
    setSelectedChoice(null);

    // ダンジョンクリア判定
    if (choices === null) {
      const partyState = state.activeParty?.members?.map(m => ({
        uniqueId: m.uniqueId,
        name: m.name,
        attribute: m.attribute,
        level: m.level || 1,
        currentHp: m.stats?.currentHp || 0,
        maxHp: m.stats?.maxHp || 0,
      })) || [];
      const summary = generateDungeonSummary(dungeon);
      actions.endDungeon({
        ...summary,
        partyState,
        isVictory: true,
      });
    }
  }, [dungeon, state.activeParty, actions]);

  /**
   * フロア選択肢を選択
   * @param {Object} choice - 選択した選択肢
   */
  const selectFloorChoice = useCallback((choice) => {
    if (!dungeon) return;

    setSelectedChoice(choice);

    // ダンジョン状態を更新
    const updatedDungeon = advanceFloor(dungeon, choice);
    actions.updateDungeon(updatedDungeon);

    // 選択肢のタイプに応じた処理
    switch (choice.type) {
      case 'battle':
      case 'strong_enemy':
      case 'boss': {
        // 経験値を敵レベルから計算（仕様通り）
        const calculatedExp = calculateBattleExp(choice.enemies, choice.type);
        console.log('[DEBUG useDungeon] calculatedExp:', calculatedExp);
        console.log('[DEBUG useDungeon] choice.enemies:', choice.enemies);
        console.log('[DEBUG useDungeon] choice.type:', choice.type);
        // バトル画面へ
        actions.startBattle({
          dungeonFloor: updatedDungeon.currentFloor,
          battleType: choice.type,
          enemies: choice.enemies,
          rewards: {
            ...choice.rewards,
            exp: calculatedExp, // 敵レベルベースの経験値で上書き
          },
        });
        break;
      }

      case 'rest': {
        // 休憩：HP回復処理（30%回復）
        const party = state.activeParty;
        const healResults = [];
        const updatedMembers = party.members.map(member => {
          if (!member || member.stats?.currentHp <= 0) return member;
          const maxHp = member.stats?.maxHp || 0;
          const currentHp = member.stats?.currentHp || 0;
          const healAmount = Math.floor(maxHp * 0.3);
          const newHp = Math.min(maxHp, currentHp + healAmount);
          const actualHeal = newHp - currentHp;
          if (actualHeal > 0) {
            healResults.push({ name: member.name, healAmount: actualHeal, newHp });
          }
          return {
            ...member,
            stats: { ...member.stats, currentHp: newHp },
          };
        });
        const healedParty = { ...party, members: updatedMembers };
        actions.setActiveParty(healedParty);

        const restResult = completeFloor(updatedDungeon, { type: 'rest', healed: true });
        actions.updateDungeon(restResult);
        setSelectedChoice(null);

        // 結果を表示
        actions.setFloorEventResult({
          type: 'rest',
          title: '🏕️ 休息',
          message: 'パーティは休息を取り、HPを回復した',
          details: healResults,
          dungeon: restResult,
        });
        break;
      }

      case 'treasure': {
        // 宝箱：報酬処理（素材ドロップを含む）
        const baseGold = choice.rewards?.gold || Math.floor(Math.random() * 500) + 100;
        const depthRatio = (updatedDungeon.currentFloor || 1) / Math.max(1, updatedDungeon.maxFloor || 10);
        const continentId = updatedDungeon.continentId || 'unknown';

        // 素材ドロップを計算
        const dropResult = rewardCalculator.calculateTreasureReward({
          continentId,
          floor: updatedDungeon.currentFloor || 1,
          maxFloor: updatedDungeon.maxFloor || 10,
          treasureType: 'normal',
        });

        const materials = dropManager.generateTreasureDrops({
          continentId,
          depthRatio,
          treasureRarity: 'normal',
        });

        // 素材を直接playerDataに保存（画面遷移で状態がリセットされる問題を回避）
        if (materials.length > 0) {
          const treasureMaterials = {};
          for (const mat of materials) {
            treasureMaterials[mat.type] = (treasureMaterials[mat.type] || 0) + mat.count;
          }
          console.log('[DEBUG treasure] 素材を即時保存:', treasureMaterials);
          updatePlayerData?.((prev) => {
            const currentMaterials = prev.twinKey?.materials || {};
            const updatedMaterials = { ...currentMaterials };
            for (const [type, count] of Object.entries(treasureMaterials)) {
              updatedMaterials[type] = (updatedMaterials[type] || 0) + count;
            }
            return {
              ...prev,
              twinKey: {
                ...prev.twinKey,
                materials: updatedMaterials,
              },
            };
          });
        }

        // ゴールドを直接playerDataに保存
        const totalGold = baseGold + (dropResult.gold || 0);
        if (totalGold > 0) {
          updatePlayerData?.((prev) => ({
            ...prev,
            twinKey: { ...prev.twinKey, gold: (prev.twinKey?.gold || 0) + totalGold },
          }));
        }

        const rewards = {
          gold: totalGold,
          materials: materials,
        };

        const treasureResult = completeFloor(updatedDungeon, { type: 'treasure', rewards });
        actions.updateDungeon(treasureResult);
        setSelectedChoice(null);

        // 結果を表示
        actions.setFloorEventResult({
          type: 'treasure',
          title: '💎 宝箱発見',
          message: '宝箱から報酬を獲得した！',
          details: rewards,
          dungeon: treasureResult,
        });
        break;
      }

      case 'event': {
        // イベント：ランダム効果（良いか悪いか）
        const isGoodEvent = Math.random() > 0.4; // 60%で良いイベント
        let eventMessage = '';
        let eventDetails = {};
        const party = state.activeParty;

        if (isGoodEvent) {
          // 良いイベント：HP回復、攻撃力UP、ゴールド獲得など
          const eventType = Math.floor(Math.random() * 3);
          if (eventType === 0) {
            // 全員HP回復
            const healPercent = 0.2;
            const updatedMembers = party.members.map(member => {
              if (!member || member.stats?.currentHp <= 0) return member;
              const maxHp = member.stats?.maxHp || 0;
              const newHp = Math.min(maxHp, member.stats.currentHp + Math.floor(maxHp * healPercent));
              return { ...member, stats: { ...member.stats, currentHp: newHp } };
            });
            actions.setActiveParty({ ...party, members: updatedMembers });
            eventMessage = '旅人に出会い、回復薬を分けてもらった！';
            eventDetails = { effect: 'HP20%回復' };
          } else if (eventType === 1) {
            // ゴールド獲得
            const gold = Math.floor(Math.random() * 300) + 100;
            eventMessage = '隠された宝を発見した！';
            eventDetails = { gold };
          } else {
            // 次の戦闘で攻撃力UP（メッセージのみ）
            eventMessage = '神秘的な泉の水を飲んだ。力がみなぎる！';
            eventDetails = { effect: '気持ちが高揚した' };
          }
        } else {
          // 悪いイベント：少量ダメージ、デバフなど
          const eventType = Math.floor(Math.random() * 2);
          if (eventType === 0) {
            // 全員に少量ダメージ
            const damagePercent = 0.1;
            const damageResults = [];
            const updatedMembers = party.members.map(member => {
              if (!member || member.stats?.currentHp <= 0) return member;
              const maxHp = member.stats?.maxHp || 0;
              const damage = Math.floor(maxHp * damagePercent);
              const newHp = Math.max(1, member.stats.currentHp - damage);
              damageResults.push({ name: member.name, damage });
              return { ...member, stats: { ...member.stats, currentHp: newHp } };
            });
            actions.setActiveParty({ ...party, members: updatedMembers });
            eventMessage = '足場が崩れた！パーティはダメージを受けた';
            eventDetails = { damages: damageResults };
          } else {
            eventMessage = '不気味な気配を感じた...何も起こらなかった';
            eventDetails = { effect: 'なし' };
          }
        }

        const eventResult = completeFloor(updatedDungeon, { type: 'event', effect: choice.effect });
        actions.updateDungeon(eventResult);
        setSelectedChoice(null);

        // 結果を表示
        actions.setFloorEventResult({
          type: 'event',
          title: isGoodEvent ? '✨ 良いイベント' : '💨 イベント',
          message: eventMessage,
          details: eventDetails,
          dungeon: eventResult,
        });
        break;
      }

      case 'trap': {
        // 罠：ダメージ or デバフ
        const trap = choice.actualTrap || { name: '隠し罠', damagePercent: 0.15 };
        const party = state.activeParty;
        const damageResults = [];
        const updatedMembers = party.members.map(member => {
          if (!member || member.stats?.currentHp <= 0) return member;
          const maxHp = member.stats?.maxHp || 0;
          const damage = Math.floor(maxHp * (trap.damagePercent || 0.15));
          const newHp = Math.max(1, member.stats.currentHp - damage);
          damageResults.push({ name: member.name, damage, newHp });
          return { ...member, stats: { ...member.stats, currentHp: newHp } };
        });
        actions.setActiveParty({ ...party, members: updatedMembers });

        const trapResult = completeFloor(updatedDungeon, { type: 'trap', trap });
        actions.updateDungeon(trapResult);
        setSelectedChoice(null);

        // 結果を表示
        actions.setFloorEventResult({
          type: 'trap',
          title: '⚠️ 罠発動',
          message: `${trap.name || '罠'}が発動した！`,
          details: { damages: damageResults },
          dungeon: trapResult,
        });
        break;
      }

      default:
        break;
    }

    return { choice, dungeon: updatedDungeon };
  }, [dungeon, actions, state.activeParty, collectedMaterials, dropManager]);

  /**
   * フロアを完了
   * @param {Object} result - フロア結果
   */
  const completeCurrentFloor = useCallback((result) => {
    if (!dungeon) return;

    console.log('[DEBUG completeCurrentFloor] result:', result);
    console.log('[DEBUG completeCurrentFloor] result.exp:', result.exp);

    const updatedDungeon = completeFloor(dungeon, result);
    actions.updateDungeon(updatedDungeon);

    // ============================================================
    // 戦闘後のHP同期（全バトル結果で共通処理）
    // React状態更新は非同期なので、allyHpStatusを使って直接適用
    // 死亡したモンスターはHP1で復活させる
    // ============================================================
    let hpSyncedMembers = state.activeParty?.members || [];
    if (result.allyHpStatus && result.allyHpStatus.length > 0 && state.activeParty) {
      hpSyncedMembers = state.activeParty.members.map(member => {
        const hpData = result.allyHpStatus.find(
          h => h.uniqueId === member.uniqueId || h.cardId === member.cardId || h.name === member.name
        );
        if (hpData) {
          // 死亡状態（HP0またはisAlive=false）の場合はHP1で復活
          const wasDeadOrZeroHp = !hpData.isAlive || hpData.currentHp <= 0;
          const newHp = wasDeadOrZeroHp ? 1 : hpData.currentHp;
          console.log(`[HP同期・復活] ${member.name}: HP=${hpData.currentHp}, isAlive=${hpData.isAlive} -> HP=${newHp}, isAlive=true`);
          return {
            ...member,
            stats: {
              ...member.stats,
              currentHp: newHp,
            },
            isAlive: true, // HP1で復活させるので常にtrue
          };
        }
        return member;
      });

      // 逃走・敗北時はここでパーティを更新（勝利時は経験値処理内で更新される）
      if (result.type !== 'battle_won') {
        const syncedParty = {
          ...state.activeParty,
          members: hpSyncedMembers,
          updatedAt: Date.now(),
        };
        actions.setActiveParty(syncedParty);
      }
    }

    // 今回のバトルで獲得した素材とゴールド（関数スコープで保持）
    let battleMaterials = {};
    let battleGold = 0;

    // 経験値適用と素材ドロップ（戦闘勝利時）
    if (result.type === 'battle_won' && result.exp > 0 && state.activeParty) {
      console.log('[DEBUG completeCurrentFloor] 経験値適用開始');

      // 事前計算済みドロップがあればそれを使用、なければ再計算
      let materials = result.preCalculatedDrops || [];

      if (materials.length === 0) {
        // 素材ドロップを計算（ハイブリッドシステム使用）
        const continentId = dungeon?.continentId || 'unknown';
        const depthRatio = (dungeon?.currentFloor || 1) / Math.max(1, dungeon?.maxFloor || 10);
        const battleType = result.battleType || 'normal';
        const rawEnemies = result.enemies || [];

        // バトルユニットからcardId/categoryを抽出（sourceDataから取得）
        const enemies = rawEnemies.map(e => ({
          ...e,
          cardId: e.cardId || e.sourceData?.cardId,
          category: e.category || e.sourceData?.category,
          name: e.name || e.sourceData?.name,
        }));

        console.log('[DEBUG completeCurrentFloor] enemies with sourceData:', enemies.map(e => ({
          name: e.name,
          cardId: e.cardId,
          category: e.category,
          hasSourceData: !!e.sourceData,
        })));

        if (enemies.length > 0 && enemies.some(e => e.cardId || e.category)) {
          // ハイブリッドドロップ（カードID/カテゴリ/大陸の優先度）
          materials = dropManager.generateMultiEnemyDrops({
            enemies,
            continentId,
            depthRatio,
            battleType,
          });
        } else {
          // フォールバック: 従来の大陸ベースドロップ
          materials = dropManager.generateBattleDrops({
            continentId,
            depthRatio,
            battleType,
            enemies,
          });
        }
      }

      console.log('[DEBUG completeCurrentFloor] 素材ドロップ:', materials);

      // 素材を直接playerDataに保存（画面遷移で状態がリセットされる問題を回避）
      if (materials.length > 0) {
        for (const mat of materials) {
          const materialType = mat.item || mat.type;
          battleMaterials[materialType] = (battleMaterials[materialType] || 0) + mat.count;
        }
        // 即座にplayerDataに保存
        console.log('[DEBUG completeCurrentFloor] 素材を即時保存:', battleMaterials);
        updatePlayerData?.((prev) => {
          const currentMaterials = prev.twinKey?.materials || {};
          const updatedMaterials = { ...currentMaterials };
          for (const [type, count] of Object.entries(battleMaterials)) {
            updatedMaterials[type] = (updatedMaterials[type] || 0) + count;
          }
          return {
            ...prev,
            twinKey: {
              ...prev.twinKey,
              materials: updatedMaterials,
            },
          };
        });
      }

      // ゴールドを直接playerDataに保存
      battleGold = result.gold || 0;
      if (battleGold > 0) {
        updatePlayerData?.((prev) => ({
          ...prev,
          twinKey: { ...prev.twinKey, gold: (prev.twinKey?.gold || 0) + battleGold },
        }));
      }

      const party = state.activeParty;

      // HP同期済みメンバーを使用（共通処理で適用済み）
      const aliveMembers = hpSyncedMembers.filter(m => m.isAlive !== false);
      const expPerMember = Math.floor(result.exp / Math.max(1, aliveMembers.length));

      // 各メンバーに経験値を適用（HP同期済みメンバーをベースにする）
      const updatedMembers = hpSyncedMembers.map(member => {
        // 死亡扱いだったメンバーも経験値は獲得する（HP1で復活済み）
        const oldLevel = member.level || 1;
        const expResult = addExperience(
          { level: oldLevel, exp: member.exp || 0, awakening: member.awakening || 0 },
          expPerMember,
          member.rarity || 'C'
        );

        // レベルが上がった場合はステータスを再計算
        if (expResult.level > oldLevel) {
          const cardData = {
            attack: member.baseAttack,
            hp: member.baseHp,
          };
          const newStats = calculateAllStats(
            cardData,
            expResult.level,
            member.rarity || 'C',
            member.awakening || 0
          );

          return {
            ...member,
            level: expResult.level,
            exp: expResult.exp,
            stats: {
              ...member.stats,
              attack: newStats.attack,
              maxHp: newStats.maxHp,
              currentHp: Math.min(member.stats.currentHp, newStats.maxHp), // 現在HPは維持（上限まで）
              defense: newStats.defense,
              speed: newStats.speed,
            },
          };
        }

        return {
          ...member,
          level: expResult.level,
          exp: expResult.exp,
        };
      });

      const updatedParty = {
        ...party,
        members: updatedMembers,
        updatedAt: Date.now(),
      };

      actions.setActiveParty(updatedParty);

      // モンスター成長データを永続化（useParty.jsと同じパターン）
      updatePlayerData?.((prev) => {
        const currentProgress = prev.twinKey?.monsterProgress || {};
        const updatedProgress = { ...currentProgress };

        // 各メンバーのレベル・経験値を保存
        for (const member of updatedMembers) {
          if (member && member.cardId) {
            updatedProgress[member.cardId] = {
              level: member.level || 1,
              exp: member.exp || 0,
              awakening: member.awakening || 0,
            };
          }
        }

        // パーティデータも更新
        const currentParties = prev.twinKey?.parties || [];
        const updatedParties = currentParties.map(p =>
          p.id === updatedParty.id ? updatedParty : p
        );

        return {
          ...prev,
          twinKey: {
            ...prev.twinKey,
            monsterProgress: updatedProgress,
            parties: updatedParties,
          },
        };
      });
    }

    // パーティ状態を取得（結果画面用）
    const partyState = state.activeParty?.members?.map(m => ({
      uniqueId: m.uniqueId,
      name: m.name,
      attribute: m.attribute,
      level: m.level || 1,
      currentHp: m.stats?.currentHp || 0,
      maxHp: m.stats?.maxHp || 0,
    })) || [];

    // 敗北判定
    if (result.type === 'battle_lost' || result.type === 'party_wiped') {
      const summary = generateDungeonSummary(updatedDungeon);
      // 敗北時も宝箱で収集した素材は保存（バトル素材は既に上で直接保存済み）
      saveMaterialsToPlayerData();
      actions.endDungeon({
        ...summary,
        partyState,
        collectedMaterials,
        collectedGold,
        isVictory: false,
        isDefeat: true,
      });
      return;
    }

    // ダンジョンクリア判定（updatedDungeonを使用）
    if (updatedDungeon.isCompleted) {
      const summary = generateDungeonSummary(updatedDungeon);
      // 宝箱で収集した素材を保存（バトル素材は既に上で直接保存済み）
      saveMaterialsToPlayerData();
      actions.endDungeon({
        ...summary,
        partyState,
        collectedMaterials,
        collectedGold,
        isVictory: true,
      });
      return;
    }

    // 次のフロアへ
    setSelectedChoice(null);
    // updatedDungeonで次のフロア選択肢を取得
    const nextChoices = getNextFloorChoices(updatedDungeon);
    if (nextChoices === null) {
      // ダンジョンクリア
      const summary = generateDungeonSummary(updatedDungeon);
      // 宝箱で収集した素材を保存（バトル素材は既に上で直接保存済み）
      saveMaterialsToPlayerData();
      actions.endDungeon({
        ...summary,
        partyState,
        collectedMaterials,
        collectedGold,
        isVictory: true,
      });
    } else {
      setFloorChoices(nextChoices);
      // ダンジョン画面へ遷移（バトル画面から戻る）
      actions.setScreen('dungeon');
    }
  }, [dungeon, state.activeParty, actions, updatePlayerData, collectedMaterials, collectedGold, dropManager, saveMaterialsToPlayerData]);

  // ============================================================
  // 休憩・イベント処理
  // ============================================================

  /**
   * 休憩処理
   * @param {Array} partyMembers - パーティメンバー
   * @param {number} healPercent - 回復割合
   * @returns {Object} 回復結果
   */
  const processRest = useCallback((partyMembers, healPercent = 0.3) => {
    const healResults = partyMembers.map(member => {
      if (!member || !member.isAlive) return null;

      const healAmount = Math.floor(member.stats.maxHp * healPercent);
      const newHp = Math.min(member.stats.maxHp, member.stats.currentHp + healAmount);
      const actualHeal = newHp - member.stats.currentHp;

      return {
        memberId: member.id,
        name: member.name,
        healAmount: actualHeal,
        newHp,
      };
    }).filter(Boolean);

    return { healResults };
  }, []);

  /**
   * 宝箱処理
   * @param {Object} treasure - 宝箱情報
   * @returns {Object} 報酬結果
   */
  const processTreasure = useCallback((treasure) => {
    // ランダム報酬生成（簡易版）
    const gold = treasure.rewards?.gold || 0;
    const itemChance = treasure.rewards?.itemChance || 0;
    const gotItem = Math.random() < itemChance;

    return {
      gold,
      gotItem,
      item: gotItem ? { name: 'ランダムアイテム', rarity: 'C' } : null,
    };
  }, []);

  /**
   * 罠処理
   * @param {Object} trap - 罠情報
   * @param {Array} partyMembers - パーティメンバー
   * @returns {Object} 罠結果
   */
  const processTrap = useCallback((trap, partyMembers) => {
    const actualTrap = trap.actualTrap || trap;
    const effect = actualTrap.effect;

    const results = {
      trapName: actualTrap.name,
      trapEffect: actualTrap.effect,
      damages: [],
    };

    if (effect.type === 'damage') {
      // HP割合ダメージ
      results.damages = partyMembers.map(member => {
        if (!member || !member.isAlive) return null;

        const damage = Math.floor(member.stats.maxHp * effect.value);
        return {
          memberId: member.id,
          name: member.name,
          damage,
        };
      }).filter(Boolean);
    }

    return results;
  }, []);

  // ============================================================
  // 撤退
  // ============================================================

  /**
   * ダンジョンから撤退
   */
  const retreat = useCallback(() => {
    if (!dungeon) return;

    const partyState = state.activeParty?.members?.map(m => ({
      uniqueId: m.uniqueId,
      name: m.name,
      attribute: m.attribute,
      level: m.level || 1,
      currentHp: m.stats?.currentHp || 0,
      maxHp: m.stats?.maxHp || 0,
    })) || [];

    const summary = generateDungeonSummary({
      ...dungeon,
      isCompleted: false,
      isFailed: false,
    });

    // 撤退時も収集した素材は保存
    saveMaterialsToPlayerData();

    actions.endDungeon({
      ...summary,
      partyState,
      collectedMaterials,
      collectedGold,
      isVictory: false,
      isDefeat: false,
      isRetreated: true,
    });
  }, [dungeon, state.activeParty, actions, collectedMaterials, collectedGold, saveMaterialsToPlayerData]);

  /**
   * フロアイベント結果から次のフロアへ進む
   */
  const continueFromFloorEvent = useCallback(() => {
    const floorEventResult = state.floorEventResult;
    if (!floorEventResult) return;

    const eventDungeon = floorEventResult.dungeon;
    actions.clearFloorEventResult();

    // パーティ状態を取得（結果画面用）
    const partyState = state.activeParty?.members?.map(m => ({
      uniqueId: m.uniqueId,
      name: m.name,
      attribute: m.attribute,
      level: m.level || 1,
      currentHp: m.stats?.currentHp || 0,
      maxHp: m.stats?.maxHp || 0,
    })) || [];

    // 次のフロアの選択肢を取得
    const nextChoices = getNextFloorChoices(eventDungeon);
    if (nextChoices === null) {
      // ダンジョンクリア
      const summary = generateDungeonSummary(eventDungeon);
      saveMaterialsToPlayerData();
      actions.endDungeon({
        ...summary,
        partyState,
        collectedMaterials,
        collectedGold,
        isVictory: true,
      });
    } else {
      setFloorChoices(nextChoices);
    }
  }, [state.floorEventResult, state.activeParty, actions, collectedMaterials, collectedGold, saveMaterialsToPlayerData]);

  // ============================================================
  // 返却値
  // ============================================================

  // 探索中かどうか
  const isExploring = !!dungeon;

  return {
    // 状態
    dungeon,
    currentFloor,
    maxFloor,
    progress,
    isBossFloor,
    isExploring,
    floorChoices,
    selectedChoice,
    hoveredChoice,
    setHoveredChoice,

    // 収集した報酬
    collectedMaterials,
    collectedGold,

    // フロア進行
    loadNextFloorChoices,
    selectFloorChoice,
    completeCurrentFloor,
    continueFromFloorEvent,

    // イベント処理
    processRest,
    processTreasure,
    processTrap,

    // 撤退
    retreat,
  };
}

export default useDungeon;
